import {Box, Chip, Paper, Popper, Typography} from '@mui/material'
import type {FC, MouseEvent as ReactMouseEvent} from 'react'
import {useState} from 'react'
import AppbarSearchHierarchyFilter from './AppbarSearchHierarchyFilter.tsx'
import SearchEquipmentResults from './SearchEquipmentResults.tsx'
import SearchEquipmentTimeWindowResults from './SearchEquipmentTimeWindowResults.tsx'
import SearchLineResults from './SearchLineResults.tsx'
import type {AppbarHierarchyFilterState} from './useAppbarHierarchyFilter.ts'
import {
  EMPTY_APPBAR_HIERARCHY_FILTER_STATE,
  useAppbarHierarchyFilter,
} from './useAppbarHierarchyFilter.ts'
import HorizontalScrollingContainer from '@shared/components/HorizontalScrollingContainer.tsx'
import ScrollbarContainer from '@shared/components/ScrollbarContainer.tsx'
import {useCustomTranslation} from '@shared/hooks/useCustomTranslation.ts'

interface AppbarSearchStandardProps {
  anchorEl: HTMLElement | null
  minWidth: number
  handleClose: () => void
  searchTerm: string
  hierarchyFilterState: AppbarHierarchyFilterState
  onHierarchyFilterStateChange: (next: AppbarHierarchyFilterState) => void
}

const AppbarSearchStandard: FC<AppbarSearchStandardProps> = ({
  anchorEl,
  minWidth,
  handleClose,
  searchTerm,
  hierarchyFilterState,
  onHierarchyFilterStateChange,
}) => {
  const [selectedChips, setSelectedChips] = useState<Set<string>>(new Set())

  const [t] = useCustomTranslation('common')

  const {
    updateParam,
    searchHandlers,
    data: {productionAreas, lines, equipments},
    loading,
    selectedNames,
    paramKeys,
  } = useAppbarHierarchyFilter(hierarchyFilterState, onHierarchyFilterStateChange)

  const activeHierarchyFilterCount = [
    hierarchyFilterState.productionAreaId,
    hierarchyFilterState.lineId,
    hierarchyFilterState.equipmentId,
  ].filter(Boolean).length

  // Every genuine result action (title, image, warning/status link, or an ActionButton)
  // renders as an <a> or <button>. Closing on any click anywhere in the results Box -
  // plain text like "N found", whitespace, card backgrounds - closed the whole popper
  // on completely inert clicks, including ones that land here after dragging the
  // horizontal scrollbar. Only close when a real result was actually clicked.
  const handleResultsClick = (event: ReactMouseEvent<HTMLElement>): void => {
    if ((event.target as HTMLElement).closest('a, button')) {
      handleClose()
    }
  }

  const handleChipClick = (chipKey: string): void => {
    setSelectedChips((prevSelectedChips) => {
      const newSelectedChips = new Set(prevSelectedChips)
      if (newSelectedChips.has(chipKey)) {
        newSelectedChips.delete(chipKey)
      } else {
        newSelectedChips.add(chipKey)
      }
      return newSelectedChips
    })
  }

  return (
    <Popper
      id="mbai-standard-search-popper"
      open
      anchorEl={anchorEl}
      placement="bottom-start"
      style={{
        zIndex: 1300,
        minWidth,
        maxWidth: '70%',
        maxHeight: '70vh',
      }}
    >
      <Paper sx={{p: 2}} elevation={8}>
        <Typography
          variant="h6"
          color="text.secondary"
          // Add some spacing to the bottom
          sx={{mb: 1}}
        >
          {t('appbar_search_categories.title')}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1,
          }}
        >
          {/* Outside the scrolling container below: it's a fixed control, not a
              scrollable chip, and its badge pokes past the icon's own box --
              the scroll container's overflow was clipping it (AB#6170). */}
          <AppbarSearchHierarchyFilter
            filterState={hierarchyFilterState}
            activeCount={activeHierarchyFilterCount}
            productionAreas={productionAreas}
            lines={lines}
            equipments={equipments}
            searchHandlers={searchHandlers}
            loading={loading}
            selectedNames={selectedNames}
            paramKeys={paramKeys}
            updateParam={updateParam}
            onClear={() => onHierarchyFilterStateChange(EMPTY_APPBAR_HIERARCHY_FILTER_STATE)}
          />
          <HorizontalScrollingContainer>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Chip
                key="lines"
                label={t('appbar_search_categories.lines')}
                onClick={() => handleChipClick('lines')}
                color={selectedChips.has('lines') ? 'primary' : 'default'}
              />
              <Chip
                key="equipments"
                label={t('appbar_search_categories.equipments')}
                onClick={() => handleChipClick('equipments')}
                color={selectedChips.has('equipments') ? 'primary' : 'default'}
              />
              <Chip
                key="time_windows"
                label={t('appbar_search_categories.time_windows')}
                onClick={() => handleChipClick('time_windows')}
                color={selectedChips.has('time_windows') ? 'primary' : 'default'}
              />
            </Box>
          </HorizontalScrollingContainer>
        </Box>
        {activeHierarchyFilterCount > 0 && selectedNames.mostSpecificSelection ? (
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="mbai-appbar-search-hierarchy-filter-indicator"
            sx={{mb: 1}}
          >
            {t('appbar_search_categories.showing_results_for', {
              scope: selectedNames.mostSpecificSelection,
            })}
          </Typography>
        ) : null}
        <ScrollbarContainer maxHeight="calc(100vh - 200px)">
          {searchTerm ? (
            <Box>
              {selectedChips.size === 0 || selectedChips.has('lines') ? (
                <Box onClick={handleResultsClick}>
                  <SearchLineResults
                    searchTerm={searchTerm}
                    productionAreaId={hierarchyFilterState.productionAreaId}
                  />
                </Box>
              ) : null}
              {selectedChips.size === 0 || selectedChips.has('equipments') ? (
                <Box onClick={handleResultsClick}>
                  <SearchEquipmentResults
                    searchTerm={searchTerm}
                    productionAreaId={hierarchyFilterState.productionAreaId}
                    lineId={hierarchyFilterState.lineId}
                    equipmentId={hierarchyFilterState.equipmentId}
                  />
                </Box>
              ) : null}
              {selectedChips.size === 0 || selectedChips.has('time_windows') ? (
                <Box onClick={handleResultsClick}>
                  <SearchEquipmentTimeWindowResults
                    searchTerm={searchTerm}
                    productionAreaId={hierarchyFilterState.productionAreaId}
                    lineId={hierarchyFilterState.lineId}
                    equipmentId={hierarchyFilterState.equipmentId}
                  />
                </Box>
              ) : null}
            </Box>
          ) : null}
        </ScrollbarContainer>
      </Paper>
    </Popper>
  )
}

export default AppbarSearchStandard
